import {
  clippingOuterRings,
  footprintPolygonsToClipping
} from '@alpinehomeair/common';
import { FootprintShapeType, LevelType, PositionType } from 'data/types';
import polygonClipping from 'polygon-clipping';
import { calculateFootprintArea } from 'utils/footprint-scaling';

export type FootprintUnionResult = {
  outline: PositionType[];
  outlines: PositionType[][];
  isDisjoint: boolean;
  enclosesHole: boolean;
};

const EMPTY_UNION: FootprintUnionResult = {
  outline: [],
  outlines: [],
  isDisjoint: false,
  enclosesHole: false
};

export function isValidFootprintShape(shape: PositionType[]): boolean {
  return shape.length >= 3;
}

function largestRing(rings: PositionType[][]): PositionType[] {
  return rings.reduce((largest, ring) =>
    calculateFootprintArea(ring) > calculateFootprintArea(largest)
      ? ring
      : largest
  );
}

export function unionFootprintShapes(
  shapes: PositionType[][]
): FootprintUnionResult {
  const validShapes = shapes.filter(isValidFootprintShape);
  if (validShapes.length === 0) return EMPTY_UNION;

  let outlines: PositionType[][];
  let enclosesHole = false;
  try {
    const [first, ...rest] = footprintPolygonsToClipping(validShapes);
    const merged = polygonClipping.union(first, ...rest);
    outlines = clippingOuterRings(merged);
    enclosesHole = merged.some((polygon) => polygon.length > 1);
  } catch {
    outlines = validShapes.map((shape) => shape.map((p) => ({ ...p })));
  }

  if (outlines.length === 0) return EMPTY_UNION;

  return {
    outline: largestRing(outlines),
    outlines,
    isDisjoint: outlines.length > 1,
    enclosesHole
  };
}

export function footprintShapesEncloseHole(shapes: PositionType[][]): boolean {
  return unionFootprintShapes(shapes).enclosesHole;
}

export function sanitizeEditedOutline(
  points: PositionType[]
): PositionType[] | null {
  if (!isValidFootprintShape(points)) return null;
  const { outline } = unionFootprintShapes([points]);
  return isValidFootprintShape(outline) ? outline : null;
}

export function outlineToFootprintShapes(
  outline: PositionType[]
): FootprintShapeType[] | null {
  if (!isValidFootprintShape(outline)) return null;
  return [
    {
      points: outline.map((point) => ({ ...point })),
      vertexEdited: true
    }
  ];
}

function normalizeFootprintShape(
  shape: FootprintShapeType | PositionType[]
): FootprintShapeType {
  if (Array.isArray(shape)) {
    return {
      points: shape.map((p) => ({ ...p })),
      vertexEdited: true
    };
  }
  return {
    points: (shape.points ?? []).map((p) => ({ ...p })),
    vertexEdited: shape.vertexEdited
  };
}

export function footprintShapesToPoints(
  shapes: FootprintShapeType[]
): PositionType[][] {
  return shapes.map((shape) => shape.points);
}

export function getEffectiveFootprintShapes(
  level: LevelType | null | undefined
): FootprintShapeType[] {
  if (!level) return [];
  const shapes = (level.footprintShapes ?? [])
    .map(normalizeFootprintShape)
    .filter((shape) => isValidFootprintShape(shape.points));
  if (shapes.length > 0) {
    return shapes;
  }
  const footprint = level.footprint ?? [];
  if (isValidFootprintShape(footprint)) {
    return [
      {
        points: footprint.map((p) => ({ ...p })),
        vertexEdited: true
      }
    ];
  }
  return [];
}

export function cloneFootprintShapes(
  shapes: FootprintShapeType[]
): FootprintShapeType[] {
  return shapes.map((shape) => ({
    ...shape,
    points: shape.points.map((p) => ({ ...p }))
  }));
}

export function getShapeBboxCenter(shape: PositionType[]): PositionType {
  const xs = shape.map((p) => p.x);
  const ys = shape.map((p) => p.y);
  return {
    x: (Math.min(...xs) + Math.max(...xs)) / 2,
    y: (Math.min(...ys) + Math.max(...ys)) / 2
  };
}

export function translateShapePoints(
  shape: PositionType[],
  dx: number,
  dy: number
): PositionType[] {
  return shape.map((p) => ({ ...p, x: p.x + dx, y: p.y + dy }));
}

export function translateShapeCenterTo(
  shape: PositionType[],
  target: PositionType
): PositionType[] {
  if (shape.length === 0) return shape;
  const center = getShapeBboxCenter(shape);
  const dx = Math.round(target.x - center.x);
  const dy = Math.round(target.y - center.y);
  return translateShapePoints(shape, dx, dy);
}
